import { Operation } from '~/interfaces/device.interface';
import { DevicesModule } from '~/store/modules/devices';
import { UserModule } from '~/store/modules/user';
import { PopupModule } from '~common-store/modules/popup';

export function electricalReset(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Electrical Reset',
        action: 'electricalReset',
    } as Operation;
}

export function homekitReset(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Homekit Reset',
        action: 'homekitReset',
    } as Operation;
}

export function enableSsh(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Activate Ssh',
        action: 'enableSsh',
    } as Operation;
}

export function deleteCache(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Delete Cache',
        action: 'deleteCache',
    } as Operation;
}

export function dissociate(context: {
    callback: () => Promise<any>,
    disabled: { value: boolean, reason?: string },
}) {
    return {
        ...context,
        name: 'Dissociate',
        action: 'dissociate',
        tooltip : 'To remove the device from the house',
    } as Operation;
}

export function dump(context: { callback: () => Promise<any> }) {
    async function executeDump() {
        try {
            const dumpValue = await context.callback();
            DevicesModule.setDumpValue(dumpValue);
            PopupModule.setPopupRequested('operation-dump');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Dump',
        action: 'dump',
        callback: executeDump
    } as Operation;
}

export function dumpLogs(context: { callback: () => Promise<any> }) {
    async function executeDumpLogs() {
        try {
            const dumpLogsValue = await context.callback();
            DevicesModule.setDumpLogsValue(dumpLogsValue);
            PopupModule.setPopupRequested('operation-dump-logs');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Dump Logs',
        action: 'dumpLogs',
        callback: executeDumpLogs,
    } as Operation;
}

export function associateUser(context: { disabled: { value: boolean, reason: string } }) {
    async function executeAssociateUser() {
        try {
            PopupModule.setPopupRequested('associate-user');
        } catch(error) {
            console.error(error);
        }
    }
    return {
        name: 'Associate User',
        action: 'associateUser',
        callback: executeAssociateUser,
        disabled: context.disabled
    } as Operation;
}

export function fullReset(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Full Reset',
        action: 'fullReset',
    } as Operation;
}

export function getLocalResources(context: { callback: () => Promise<any> }) {
    async function executeGetLocalResources() {
        try {
            const localResources = await context.callback();
            DevicesModule.setLocalResources(localResources);
            PopupModule.setPopupRequested('operation-get-local-resources');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Get Local Resources',
        action: 'getLocalResources',
        callback: executeGetLocalResources,
    } as Operation;
}

export function getOEMInfos(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'OEM Infos',
        action: 'getOEMInfos',
    } as Operation;
}

export function importData(context: {
    callback: (params: { device_id: string, module_id?: string }) => Promise<any>,
    params: { device_id: string, type: string, module_id?: string },
}) {
    async function executeImportData() {
        try {
            const migrationData = await context.callback(context.params);
            DevicesModule.setMigrationData(migrationData);
            PopupModule.setPopupRequested('operation-import-data');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Import Data',
        action: 'importData',
        hidden: !UserModule.userMail,
        callback: executeImportData,
    } as Operation;
}

export function installQRCode(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Install QRCode',
        action: 'installQRCode',
    } as Operation;
}

export function logs(context: { callback: () => Promise<any> }) {
    async function executeLogs() {
        try {
            const logsValue = await context.callback();
            DevicesModule.setLogsValue(logsValue);
            PopupModule.setPopupRequested('operation-logs');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Logs',
        action: 'logs',
        callback: executeLogs,
    } as Operation;
}

export function mainloop(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Mainloop',
        action: 'mainloop',
        tooltip : 'To force WIFI reconnection',
    } as Operation;
}

export function modelDump(context: { callback: () => Promise<any> }) {
    async function executeModelDump() {
        try {
            const modelDumpValue = await context.callback();
            DevicesModule.setModelDumpValue(modelDumpValue);
            PopupModule.setPopupRequested('operation-model-dump');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Model Dump',
        action: 'modelDump',
        callback: executeModelDump,
    } as Operation;
}

export function diagnosis(context: { callback: () => Promise<any> }) {
    async function executeDiagnosis() {
        try {
            const diagnosisValue = await context.callback();
            DevicesModule.setDiagnosisValue(diagnosisValue);
            PopupModule.setPopupRequested('operation-diagnosis');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Diagnosis',
        action: 'diagnosis',
        callback: executeDiagnosis,
    } as Operation;
}

export function moveToTemporaryHome(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Move to temporary home',
        action: 'moveToTemporaryHome',
    } as Operation;
}

export function orderReboot(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Order Reboot',
        action: 'orderReboot',
        tooltip : 'To reboot the gateway'
    } as Operation;
}

export function ping(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Ping',
        action: 'ping',
    } as Operation;
}

export function recalibrateCO2(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Recalibrate CO2',
        action: 'recalibrateCO2',
    } as Operation;
}

export function reset(context: { callback: (params: { device_id: string, module_id?: string }) => Promise<any> }) {
    return {
        ...context,
        name: 'Reset',
        action: 'reset',
    } as Operation;
}

export function resetQRCode(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Reset QRCode',
        action: 'resetQRCode',
    } as Operation;
}

export function rollbackMigration(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Rollback Migration',
        action: 'rollbackMigration',
    } as Operation;
}

export function setDump(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Set Dump',
        action: 'setDump',
    } as Operation;
}

async function executeStatsDump(context: any) {
    try {
        const statsDumpValue = await context.callback();
        DevicesModule.setStatsDumpValue(statsDumpValue);
        PopupModule.setPopupRequested('operation-stats-dump');
    } catch (error) {
        console.error(error);
    }
}

export function resetMigrationFlag(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Reset Migration Flag',
        action: 'resetMigrationFlag',
    } as Operation;
}

export function resetMigrationSecret(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Reset Migration Secret',
        action: 'resetMigrationSecret',
    } as Operation;
}

export function resetFlag(context: { callback: () => Promise<any> }) {
    return {
        ...context,
        name: 'Reset Flag',
        action: 'resetFlag',
    } as Operation;
}

export function statsDump(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        name: 'Stats Dump',
        action: 'statsDump',
        callback: () => executeStatsDump(context),
        disabled: context.disabled,
    } as Operation;
}

export function statsDumpVerbose(
    context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } },
) {
    return {
        name: 'Stats Dump Verbose',
        action: 'statsDumpVerbose',
        callback: () => executeStatsDump(context),
        disabled: context.disabled,
    } as Operation;
}

export function switchDatabase(context: {
    callback: () => Promise<any>,
    disabled: { value: boolean, reason?: string },
}) {
    return {
        ...context,
        name: 'Switch Database',
        action: 'switchDatabase',
    } as Operation;
}

export function triggerLogsDump(context: {
    callback: () => Promise<any>,
}) {
    return {
        ...context,
        name: 'Trigger logs dump',
        action: 'triggerLogsDump',
    } as Operation;
}

export function timeline(context: { callback: () => Promise<any> }) {
    async function executeTimeline() {
        try {
            const timelineEvents = await context.callback();
            DevicesModule.setTimelineEvents(timelineEvents);
            PopupModule.setPopupRequested('operation-timeline');
        } catch (error) {
            console.error(error);
        }
    }
    return {
        name: 'Timeline',
        action: 'timeline',
        callback: executeTimeline,
    } as Operation;
}

export function unlock(context: { callback: () => Promise<any>, disabled: { value: boolean, reason?: string } }) {
    return {
        ...context,
        name: 'Unlock',
        action: 'unlock',
    } as Operation;
}

export default {
    electricalReset,
    homekitReset,
    enableSsh,
    deleteCache,
    dissociate,
    dump,
    dumpLogs,
    fullReset,
    getLocalResources,
    getOEMInfos,
    importData,
    installQRCode,
    logs,
    mainloop,
    modelDump,
    moveToTemporaryHome,
    orderReboot,
    ping,
    recalibrateCO2,
    reset,
    resetQRCode,
    resetMigrationFlag,
    resetMigrationSecret,
    rollbackMigration,
    setDump,
    statsDump,
    statsDumpVerbose,
    switchDatabase,
    timeline,
    triggerLogsDump,
    unlock,
    associateUser,
    diagnosis,
    resetFlag
};
